
import { useState } from "react";
import { useI18n } from "../i18n";

const partnerImages = [
    {
        id: 1,
        image:
            "https://images.unsplash.com/photo-1494412519320-aa613dfb7738?auto=format&fit=crop&w=1600&q=85",
    },
    {
        id: 2,
        image:
            "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=1600&q=85",
    },
    {
        id: 3,
        image:
            "https://images.unsplash.com/photo-1519003722824-194d4455a60c?auto=format&fit=crop&w=1600&q=85",
    },
];

const Partners = () => {
    const { partners: content } = useI18n();
    const [activeIndex, setActiveIndex] = useState(0);

    const activePartner = content.items[activeIndex];
    const activePartnerImage = partnerImages[activeIndex];
    const partnerCount = content.items.length;

    const showPrevious = () => {
        setActiveIndex((current) =>
            current === 0 ? partnerCount - 1 : current - 1
        );
    };

    const showNext = () => {
        setActiveIndex((current) =>
            current === partnerCount - 1 ? 0 : current + 1
        );
    };

    return (
        <section className="relative overflow-hidden bg-white py-20 sm:py-24 lg:py-28">
            {/* Background decorations */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-40 top-20 h-96 w-96 rounded-full bg-cyan-400/[0.07]"
            />

            <div
                aria-hidden="true"
                className="pointer-events-none absolute -left-40 bottom-0 h-96 w-96 rounded-full bg-blue-400/[0.05]"
            />

            <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
                {/* Section heading */}
                <div className="mb-12 flex flex-col gap-6 md:mb-16 md:flex-row md:items-end md:justify-between">
                    <div className="max-w-2xl">
                        <div className="mb-5 inline-flex items-center gap-3">
                            <span className="h-px w-8 bg-cyan-500" />
                            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-600">
                                {content.eyebrow}
                            </span>
                        </div>

                        <h2 className="text-3xl font-bold leading-tight tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
                            {content.heading}
                            <span className="mt-2 block text-cyan-600">
                                {content.headingAccent}
                            </span>
                        </h2>

                        <p className="mt-5 max-w-xl text-sm leading-7 text-slate-600 sm:text-base">
                            {content.intro}
                        </p>
                    </div>

                    {/* Slider controls */}
                    <div className="flex items-center gap-3">
                        <span className="mr-3 text-sm font-medium text-slate-500">
                            <span className="text-slate-900">
                                {String(activeIndex + 1).padStart(2, "0")}
                            </span>
                            {" / "}
                            {String(partnerCount).padStart(2, "0")}
                        </span>

                        <button
                            type="button"
                            onClick={showPrevious}
                            aria-label={content.previous}
                            className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 transition-colors hover:border-cyan-500 hover:bg-cyan-500 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2"
                        >
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                className="h-5 w-5"
                                aria-hidden="true"
                            >
                                <path d="M19 12H5M12 19l-7-7 7-7" />
                            </svg>
                        </button>

                        <button
                            type="button"
                            onClick={showNext}
                            aria-label={content.next}
                            className="flex h-12 w-12 items-center justify-center rounded-full border border-cyan-500 bg-cyan-500 text-white transition-colors hover:border-cyan-600 hover:bg-cyan-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2"
                        >
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                className="h-5 w-5"
                                aria-hidden="true"
                            >
                                <path d="M5 12h14M12 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Featured partner */}
                <div
                    key={activePartner.id}
                    className="grid overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_20px_70px_-35px_rgba(15,23,42,0.22)] lg:grid-cols-2"
                >
                    {/* Large image */}
                    <div className="group relative min-h-[280px] overflow-hidden sm:min-h-[380px] lg:min-h-[540px]">
                        <img
                            src={activePartnerImage.image}
                            alt={activePartner.name}
                            loading="lazy"
                            decoding="async"
                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                        />

                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/10 to-transparent" />

                        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-9 lg:p-10">
                            <span className="inline-flex items-center rounded-full border border-white/30 bg-white/10 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-white backdrop-blur-sm sm:text-xs">
                                {activePartner.category}
                            </span>

                            <h3 className="mt-4 max-w-lg text-2xl font-bold leading-tight text-white sm:text-3xl lg:text-4xl">
                                {activePartner.name}
                            </h3>
                        </div>
                    </div>

                    {/* Information */}
                    <div className="flex flex-col justify-between p-6 sm:p-9 lg:p-12">
                        <div>
                            <div className="flex items-center gap-3">
                                <span className="h-8 w-1 rounded-full bg-cyan-500" />

                                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-600">
                                    {content.overview}
                                </p>
                            </div>

                            <h3 className="mt-7 text-2xl font-bold leading-snug text-slate-900 sm:text-3xl">
                                {content.overviewTitle}
                            </h3>

                            <p className="mt-6 text-sm leading-8 text-slate-600 sm:text-base">
                                {activePartner.description}
                            </p>
                        </div>

                        {/* Bottom information */}
                        <div className="mt-10 border-t border-slate-200 pt-6">
                            <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-xs text-slate-500">
                                        {content.partnership}
                                    </p>

                                    <p className="mt-1 text-sm font-semibold text-slate-900">
                                        {content.network}
                                    </p>
                                </div>

                                <div className="flex items-center gap-2 text-sm font-medium text-cyan-600">
                                    <span className="h-2 w-2 rounded-full bg-cyan-500" />
                                    {content.categoryLabel}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Slider pagination */}
                <div className="mt-8 flex items-center justify-center gap-2">
                    {content.items.map((partner, index) => (
                        <button
                            key={partner.id}
                            type="button"
                            onClick={() => setActiveIndex(index)}
                            aria-label={`${content.showPartner} ${index + 1}: ${partner.name}`}
                            aria-current={
                                index === activeIndex ? "true" : undefined
                            }
                            className={`h-2 rounded-full transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2 ${
                                index === activeIndex
                                    ? "w-9 bg-cyan-500"
                                    : "w-2 bg-slate-300 hover:bg-cyan-300"
                            }`}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Partners;
